// Estonian-specific formatting utilities

export const formatEstonianDate = (date: Date): string => {
  return new Intl.DateTimeFormat('et-EE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(date);
};

export const formatEstonianTime = (date: Date): string => {
  return new Intl.DateTimeFormat('et-EE', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(date);
};

export const formatEstonianDateTime = (date: Date): string => {
  return new Intl.DateTimeFormat('et-EE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(date);
};

export const formatEstonianCurrency = (amount: number): string => {
  return new Intl.NumberFormat('et-EE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
  }).format(amount);
};

export const formatEstonianNumber = (number: number): string => {
  return new Intl.NumberFormat('et-EE').format(number);
};

export const formatEstonianPhone = (phone: string): string => {
  // Format Estonian phone numbers
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.startsWith('372')) {
    return `+${cleaned.slice(0, 3)} ${cleaned.slice(3, 7)} ${cleaned.slice(7)}`;
  }
  if (cleaned.length === 7 || cleaned.length === 8) {
    return cleaned.replace(/(\d{3,4})(\d{4})/, '$1 $2');
  }
  return phone;
};

export const formatEstonianAddress = (address: {
  street: string;
  city: string;
  postalCode: string;
  country?: string;
}): string => {
  const { street, city, postalCode, country = 'Eesti' } = address;
  return `${street}, ${postalCode} ${city}, ${country}`;
};

// Estonian VAT calculation (20%)
export const calculateEstonianVAT = (priceWithoutVAT: number): {
  priceWithoutVAT: number;
  vat: number;
  priceWithVAT: number;
} => {
  const vatRate = 0.20; // 20% Estonian VAT
  const vat = priceWithoutVAT * vatRate;
  const priceWithVAT = priceWithoutVAT + vat;
  
  return {
    priceWithoutVAT,
    vat,
    priceWithVAT
  };
};

// Estonian business registration number validation
export const validateEstonianRegNumber = (regNumber: string): boolean => {
  const cleaned = regNumber.replace(/\s/g, '');
  return /^\d{8}$/.test(cleaned);
};

// Estonian personal ID code validation
export const validateEstonianPersonalCode = (personalCode: string): boolean => {
  const cleaned = personalCode.replace(/\s/g, '');
  if (!/^\d{11}$/.test(cleaned)) return false;
  
  // Basic checksum validation for Estonian personal codes
  const weights1 = [1, 2, 3, 4, 5, 6, 7, 8, 9, 1];
  const weights2 = [3, 4, 5, 6, 7, 8, 9, 1, 2, 3];
  
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned[i]) * weights1[i];
  }
  
  let checksum = sum % 11;
  if (checksum === 10) {
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cleaned[i]) * weights2[i];
    }
    checksum = sum % 11;
    if (checksum === 10) checksum = 0;
  }
  
  return checksum === parseInt(cleaned[10]);
};